import { DefaultAtoms } from "../ai/DefaultAtoms";
import { DefaultEntities } from "../DefaultEntities";
import { CONTEXT_KEY } from "../EntityId";
import { Roles } from "../enum/DefaultRoles";
import type { ITransaction } from "../ITransaction";
import {
  allowReadingEntities,
  type PermissionsByRole,
  readAtomPermission,
  type ValidProjectSpecificPermissions,
} from "./PermissionsByRole";

/**
 * Framework base permissions for User-scoped atoms.
 * Projects merge these into their `permissionsByRoles` with
 * `mergePermissionsByRoles(frameworkPermissionsByRoles, projectPermissions)`
 * and assign the roles with `getFrameworkRoles` in `getRolesFromContext`.
 *
 * - ApplicationUser: the censored public profile of every user
 *   (PersonalUserData/read)
 * - SelfUser: the acting user's own User entity, plus explicit read grants for
 *   every framework atom related to User. The atom grants must not rely on the
 *   entity-to-atom fallback: atoms that require explicit permissions (such as
 *   ApiTokens) ignore `User/read`. No update on ApiTokens: the token reducers
 *   of api-token-module grant it themselves, so clients cannot write token
 *   entries through the generic mutations.
 */
export const frameworkPermissionsByRoles: PermissionsByRole<
  string,
  ValidProjectSpecificPermissions
> = {
  [Roles.ApplicationUser]: [
    readAtomPermission(DefaultEntities.User, DefaultAtoms.PersonalUserData),
  ],
  [Roles.SelfUser]: [
    ...allowReadingEntities([DefaultEntities.User]),
    readAtomPermission(DefaultEntities.User, DefaultAtoms.PersonalUserData),
    readAtomPermission(DefaultEntities.User, DefaultAtoms.ApiTokens),
  ],
};

/**
 * True when the transaction's `<User>` context entry is the acting user.
 *
 * Runtimes differ in whether they normalise the casing of ids (the backend
 * lower-cases both, the frontend neither), so the comparison is
 * case-insensitive. Without an acting user nobody can claim SelfUser.
 */
function isActingOnOwnUserContext(transaction: ITransaction): boolean {
  const contextUserId = transaction.getEntry(CONTEXT_KEY(DefaultEntities.User));
  if (typeof contextUserId !== "string" || contextUserId === "") {
    return false;
  }
  let actingUserId: string;
  try {
    actingUserId = transaction.getActingUserOrThrow();
  } catch {
    return false;
  }
  return contextUserId.toLowerCase() === actingUserId.toLowerCase();
}

/**
 * Framework application roles for `getRolesFromContext`:
 * - ApplicationAdmin when `user.isAdmin`
 * - always ApplicationUser
 * - SelfUser when context `<User>` equals the acting user
 *
 * Context User is the CRUD target; actingUser is the actor — do not overwrite either.
 */
export function getFrameworkRoles(
  transaction: ITransaction,
  user: { isAdmin: boolean },
): string[] {
  const roles: string[] = [];
  if (user.isAdmin) {
    roles.push(Roles.ApplicationAdmin);
  }
  roles.push(Roles.ApplicationUser);

  if (isActingOnOwnUserContext(transaction)) {
    roles.push(Roles.SelfUser);
  }
  return roles;
}

/**
 * Concatenate permission arrays per role. Framework base first, then project
 * extras — project must not overwrite base slices for the same role key.
 */
export function mergePermissionsByRoles<
  RoleType extends string,
  PermissionsType extends ValidProjectSpecificPermissions,
>(
  base: PermissionsByRole<RoleType, PermissionsType>,
  project: PermissionsByRole<RoleType, PermissionsType>,
): PermissionsByRole<RoleType, PermissionsType> {
  const keys = new Set([
    ...Object.keys(base),
    ...Object.keys(project),
  ] as RoleType[]);
  const merged: PermissionsByRole<RoleType, PermissionsType> = {};
  for (const key of keys) {
    merged[key] = [...(base[key] ?? []), ...(project[key] ?? [])];
  }
  return merged;
}
